const API_URL = process.env.REACT_APP_HARAMBOT_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

export class HarambotApiError extends Error {
  public status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'HarambotApiError';
    this.status = status;
  }
}

export interface GuildConfig {
  guild_id?: string;
  league_id: string;
  league_type: 'nfl' | 'nba' | 'mlb';
  RIP_text?: string;
  RIP_image_url?: string;
  provider?: string;
  yahoo_authenticated?: boolean;
}

export interface LeagueInfo {
  league_id?: string;
  id?: string;
  name?: string;
  league_type?: 'nfl' | 'nba' | 'mlb' | string;
  num_teams?: number;
  season?: string;
  current_week?: number;
  [key: string]: any;
}

export type League = LeagueInfo;

export const harambotService = {
  getGuildConfig: async (guildId: string, discordToken: string): Promise<GuildConfig> => {
    const response = await fetch(`${API_URL}/config/${guildId}`, {
      method: 'GET',
      headers: {
        "X-API-Key": `${API_KEY}`,
        "Discord-Token": `${discordToken}`,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
          // Return default config if not found
          return {
              league_id: '',
              league_type: 'nfl',
              RIP_text: '',
              RIP_image_url: '',
              provider: '',
              yahoo_authenticated: false,
          };
      }
      throw new HarambotApiError(response.status, 'Failed to fetch guild configuration');
    }

    return await response.json();
  },

  updateGuildConfig: async (guildId: string, config: GuildConfig, discordToken: string): Promise<void> => {
    const response = await fetch(`${API_URL}/config/${guildId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "X-API-Key": `${API_KEY}`,
        "Discord-Token": `${discordToken}`,
      },
      body: JSON.stringify(config),
    });

    if (!response.ok) {
      throw new HarambotApiError(response.status, 'Failed to update guild configuration');
    }
  },

  getLeaguesInfo: async (guildId: string, discordToken?: string): Promise<LeagueInfo[]> => {
    const headers: Record<string, string> = {
      "X-API-Key": `${API_KEY}`,
    };
    if (discordToken) {
      headers["Discord-Token"] = `${discordToken}`;
    }

    // Stub endpoint: update URI as needed (e.g., /leagues/${guildId} or /config/${guildId}/leagues)
    const response = await fetch(`${API_URL}/leagues/${guildId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new HarambotApiError(response.status, 'Failed to fetch league information');
    }

    return await response.json();
  },

  getLeagues: async (guildId: string, discordToken?: string): Promise<LeagueInfo[]> => {
    return harambotService.getLeaguesInfo(guildId, discordToken);
  },

  getLeagueInfo: async (guildId: string, discordToken?: string): Promise<LeagueInfo[]> => {
    return harambotService.getLeaguesInfo(guildId, discordToken);
  },

  getLeague: async (guildId: string, discordToken?: string): Promise<LeagueInfo[]> => {
    return harambotService.getLeagues(guildId, discordToken);
  },
};
